/**
 * Section Block Library — 38 Pre-designed Section Presets
 *
 * Organized by category for the Visual Editor Section Library.
 * All sections use mobile-first Tailwind with CSS variables for theming.
 * Designed for Indian fashion e-commerce context.
 *
 * Categories:
 * - Products (7): Grid, Carousel, Featured, Categories, New Arrivals, Best Sellers, Comparison
 * - Hero (5): Video BG, Carousel, Countdown, Minimal Text, Fullscreen Image
 * - Content (8): Brand Story, Announcement, Trust Badges, Logos, Instagram, Lookbook, Process, Team
 * - Interactive (6): FAQ, Contact, Countdown Timer, Tabs, Newsletter, Size Guide
 * - Social/Proof (5): Reviews, Video Testimonial, Social Links, Stats, Collections
 * - E-commerce (4): Sale Banner, Shipping Info, Recently Viewed, WhatsApp CTA
 * - Featured Collection (3): Collection Showcase, Collection Banner, Categories Grid
 */

// ═══════════════════════════════════════════════════════════════════════
// PRODUCT SECTIONS (7)
// ═══════════════════════════════════════════════════════════════════════

const productSections = [

  // 1. Product Grid — 4 Column
  {
    category: 'Products',
    label: 'Product Grid — 4 Column',
    description: 'Responsive product grid with hover zoom, 2 cols mobile / 4 desktop',
    icon: 'Grid3x3',
    block: {
      id: 'product_grid_4col',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              {
                type: 'h2',
                className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3',
                content: '{{content.heading || "Our Collection"}}',
                style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text, #111827)' }
              },
              {
                type: 'p',
                className: 'text-sm md:text-base text-center mb-8 md:mb-12 max-w-2xl mx-auto',
                content: '{{content.subheading || "Handpicked styles crafted with love and precision"}}',
                style: { color: 'var(--color-text-secondary, #6b7280)' }
              },
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6',
                children: [
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Silk Kurta Set', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Silk Kurta Set', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B92,499', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Embroidered Anarkali', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Embroidered Anarkali', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B93,199', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Cotton Palazzo Set', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Cotton Palazzo Set', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B91,899', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Designer Saree', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Designer Saree', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B94,999', style: { color: 'var(--color-primary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 2. Product Carousel
  {
    category: 'Products',
    label: 'Product Carousel',
    description: 'Horizontal scrollable product cards with navigation arrows',
    icon: 'ArrowLeftRight',
    block: {
      id: 'product_carousel',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 md:py-20',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto px-4 md:px-8',
            children: [
              {
                type: 'div',
                className: 'flex items-center justify-between mb-8',
                children: [
                  { type: 'h2', className: 'text-2xl md:text-3xl font-bold', content: '{{content.heading || "Trending Now"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
                  { type: 'a', className: 'text-sm font-medium hover:underline', content: 'View All \u2192', attributes: { href: '/shop' }, style: { color: 'var(--color-primary)' } }
                ]
              },
              {
                type: 'div',
                className: 'flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide',
                style: { scrollbarWidth: 'none', msOverflowStyle: 'none' },
                children: [
                  { type: 'div', className: 'flex-shrink-0 w-[70vw] md:w-[280px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product 1', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Chanderi Silk Kurta', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B92,799', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[70vw] md:w-[280px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product 2', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Banarasi Dupatta', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B91,499', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[70vw] md:w-[280px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product 3', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Linen Palazzo Set', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B92,199', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[70vw] md:w-[280px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product 4', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Handwoven Saree', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B94,599', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[70vw] md:w-[280px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product 5', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Zari Work Lehenga', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B96,999', style: { color: 'var(--color-primary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 3. Featured Product
  {
    category: 'Products',
    label: 'Featured Product Spotlight',
    description: 'Large product image left with details, sizing & CTA right',
    icon: 'Star',
    block: {
      id: 'featured_product',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center',
            children: [
              {
                type: 'div',
                className: 'aspect-[3/4] lg:aspect-square overflow-hidden rounded-2xl bg-gray-100',
                children: [
                  { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Featured Product', loading: 'lazy' } }
                ]
              },
              {
                type: 'div',
                className: 'py-4',
                children: [
                  { type: 'span', className: 'inline-block text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4', content: 'Featured', style: { backgroundColor: 'var(--color-primary, #6366f1)', color: '#ffffff', letterSpacing: '0.1em' } },
                  { type: 'h2', className: 'text-3xl md:text-4xl lg:text-5xl font-bold mb-4', content: '{{content.productName || "Royal Chanderi Silk Kurta"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)', lineHeight: '1.15' } },
                  { type: 'p', className: 'text-base md:text-lg mb-6 leading-relaxed', content: '{{content.productDescription || "Hand-embroidered with intricate zari work, this masterpiece blends traditional Chanderi weaving techniques with contemporary silhouettes. Perfect for festive occasions and celebrations."}}', style: { color: 'var(--color-text-secondary, #6b7280)' } },
                  { type: 'div', className: 'flex items-baseline gap-3 mb-6', children: [
                    { type: 'span', className: 'text-3xl font-bold', content: '\u20B94,999', style: { color: 'var(--color-primary)' } },
                    { type: 'span', className: 'text-lg line-through', content: '\u20B97,999', style: { color: 'var(--color-text-secondary, #9ca3af)' } },
                    { type: 'span', className: 'text-sm font-semibold px-2 py-0.5 rounded bg-green-100 text-green-700', content: '38% OFF' }
                  ]},
                  { type: 'div', className: 'flex flex-wrap gap-2 mb-8', children: [
                    { type: 'span', className: 'px-4 py-2 border rounded-lg text-sm font-medium cursor-pointer hover:border-gray-900 transition-colors', content: 'S', style: { borderColor: 'var(--color-border, #e5e7eb)', color: 'var(--color-text)' } },
                    { type: 'span', className: 'px-4 py-2 border-2 rounded-lg text-sm font-medium cursor-pointer', content: 'M', style: { borderColor: 'var(--color-primary, #111827)', color: 'var(--color-text)' } },
                    { type: 'span', className: 'px-4 py-2 border rounded-lg text-sm font-medium cursor-pointer hover:border-gray-900 transition-colors', content: 'L', style: { borderColor: 'var(--color-border, #e5e7eb)', color: 'var(--color-text)' } },
                    { type: 'span', className: 'px-4 py-2 border rounded-lg text-sm font-medium cursor-pointer hover:border-gray-900 transition-colors', content: 'XL', style: { borderColor: 'var(--color-border, #e5e7eb)', color: 'var(--color-text)' } }
                  ]},
                  { type: 'a', className: 'inline-block px-8 py-3.5 rounded-xl text-base font-semibold text-white text-center transition-opacity hover:opacity-90', content: 'Add to Cart', attributes: { href: '#' }, style: { backgroundColor: 'var(--color-primary, #111827)' } }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 4. Product Categories
  {
    category: 'Products',
    label: 'Product Categories',
    description: '4 category cards with image and overlay text',
    icon: 'LayoutGrid',
    block: {
      id: 'product_categories',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3', content: '{{content.heading || "Shop by Category"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm md:text-base text-center mb-10 max-w-xl mx-auto', content: '{{content.subheading || "Find your perfect style from our curated categories"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6',
                children: [
                  { type: 'div', className: 'relative group cursor-pointer overflow-hidden rounded-xl aspect-[3/4]', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Kurtas', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent', children: [] },
                    { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-4 md:p-6', children: [
                      { type: 'h3', className: 'text-white text-lg md:text-xl font-bold', content: 'Kurtas' },
                      { type: 'p', className: 'text-white/70 text-xs md:text-sm mt-1', content: '120+ Styles' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group cursor-pointer overflow-hidden rounded-xl aspect-[3/4]', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Sarees', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent', children: [] },
                    { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-4 md:p-6', children: [
                      { type: 'h3', className: 'text-white text-lg md:text-xl font-bold', content: 'Sarees' },
                      { type: 'p', className: 'text-white/70 text-xs md:text-sm mt-1', content: '85+ Styles' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group cursor-pointer overflow-hidden rounded-xl aspect-[3/4]', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Lehengas', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent', children: [] },
                    { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-4 md:p-6', children: [
                      { type: 'h3', className: 'text-white text-lg md:text-xl font-bold', content: 'Lehengas' },
                      { type: 'p', className: 'text-white/70 text-xs md:text-sm mt-1', content: '60+ Styles' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group cursor-pointer overflow-hidden rounded-xl aspect-[3/4]', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Dupattas', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent', children: [] },
                    { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-4 md:p-6', children: [
                      { type: 'h3', className: 'text-white text-lg md:text-xl font-bold', content: 'Dupattas' },
                      { type: 'p', className: 'text-white/70 text-xs md:text-sm mt-1', content: '45+ Styles' }
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 5. New Arrivals Strip
  {
    category: 'Products',
    label: 'New Arrivals Strip',
    description: '"New Arrivals" heading with horizontal scroll product cards',
    icon: 'Sparkles',
    block: {
      id: 'new_arrivals_strip',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 md:py-20',
        style: { backgroundColor: 'var(--color-surface, #fafafa)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              {
                type: 'div',
                className: 'px-4 md:px-8 mb-8 flex items-end justify-between',
                children: [
                  { type: 'div', children: [
                    { type: 'span', className: 'inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3', content: 'Just In', style: { backgroundColor: 'var(--color-primary, #111827)', color: '#ffffff', letterSpacing: '0.15em' } },
                    { type: 'h2', className: 'text-2xl md:text-3xl font-bold', content: '{{content.heading || "New Arrivals"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } }
                  ]},
                  { type: 'a', className: 'text-sm font-medium hover:underline hidden md:block', content: 'See All New \u2192', attributes: { href: '/shop?sort=newest' }, style: { color: 'var(--color-primary)' } }
                ]
              },
              {
                type: 'div',
                className: 'flex gap-4 overflow-x-auto px-4 md:px-8 pb-4 snap-x snap-mandatory',
                style: { scrollbarWidth: 'none' },
                children: [
                  { type: 'div', className: 'flex-shrink-0 w-[65vw] md:w-[260px] snap-start', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'New Arrival 1', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/90 text-gray-900', content: 'NEW' }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm font-medium truncate', content: 'Organza Silk Dupatta', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-0.5', content: '\u20B91,299', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[65vw] md:w-[260px] snap-start', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'New Arrival 2', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/90 text-gray-900', content: 'NEW' }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm font-medium truncate', content: 'Phulkari Embroidered Top', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-0.5', content: '\u20B91,799', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[65vw] md:w-[260px] snap-start', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'New Arrival 3', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/90 text-gray-900', content: 'NEW' }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm font-medium truncate', content: 'Ikat Print Palazzo', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-0.5', content: '\u20B91,499', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[65vw] md:w-[260px] snap-start', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'New Arrival 4', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/90 text-gray-900', content: 'NEW' }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm font-medium truncate', content: 'Zardozi Work Clutch', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-sm font-semibold mt-0.5', content: '\u20B92,499', style: { color: 'var(--color-primary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 6. Best Sellers
  {
    category: 'Products',
    label: 'Best Sellers',
    description: 'Top-selling products with numbered badges and ratings',
    icon: 'Trophy',
    block: {
      id: 'best_sellers',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              { type: 'div', className: 'text-center mb-10', children: [
                { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold mb-3', content: '{{content.heading || "Best Sellers"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
                { type: 'p', className: 'text-sm md:text-base max-w-lg mx-auto', content: '{{content.subheading || "Our most-loved pieces, handpicked by thousands of happy customers"}}', style: { color: 'var(--color-text-secondary)' } }
              ]},
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6',
                children: [
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Best Seller 1', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white', content: '#1', style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Classic Silk Saree', style: { color: 'var(--color-text)' } },
                    { type: 'div', className: 'flex items-center gap-1 mt-1', children: [
                      { type: 'span', className: 'text-xs', content: '\u2605\u2605\u2605\u2605\u2605', style: { color: '#f59e0b' } },
                      { type: 'span', className: 'text-xs', content: '(248)', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B93,499', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Best Seller 2', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white', content: '#2', style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Embroidered Kurta', style: { color: 'var(--color-text)' } },
                    { type: 'div', className: 'flex items-center gap-1 mt-1', children: [
                      { type: 'span', className: 'text-xs', content: '\u2605\u2605\u2605\u2605\u2606', style: { color: '#f59e0b' } },
                      { type: 'span', className: 'text-xs', content: '(189)', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B92,199', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Best Seller 3', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white', content: '#3', style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Cotton Palazzo Set', style: { color: 'var(--color-text)' } },
                    { type: 'div', className: 'flex items-center gap-1 mt-1', children: [
                      { type: 'span', className: 'text-xs', content: '\u2605\u2605\u2605\u2605\u2605', style: { color: '#f59e0b' } },
                      { type: 'span', className: 'text-xs', content: '(156)', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B91,899', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Best Seller 4', loading: 'lazy' } },
                      { type: 'span', className: 'absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white', content: '#4', style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]},
                    { type: 'h3', className: 'mt-3 text-sm md:text-base font-medium', content: 'Banarasi Dupatta', style: { color: 'var(--color-text)' } },
                    { type: 'div', className: 'flex items-center gap-1 mt-1', children: [
                      { type: 'span', className: 'text-xs', content: '\u2605\u2605\u2605\u2605\u2606', style: { color: '#f59e0b' } },
                      { type: 'span', className: 'text-xs', content: '(134)', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'p', className: 'text-sm font-semibold mt-1', content: '\u20B91,499', style: { color: 'var(--color-primary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 7. Product Comparison
  {
    category: 'Products',
    label: 'Product Comparison',
    description: '2-3 products side by side with specs comparison',
    icon: 'Columns',
    block: {
      id: 'product_comparison',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-5xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl font-bold text-center mb-10', content: '{{content.heading || "Compare Our Bestsellers"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-3 gap-6',
                children: [
                  { type: 'div', className: 'border rounded-2xl overflow-hidden text-center', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'aspect-square bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product A', loading: 'lazy' } }
                    ]},
                    { type: 'div', className: 'p-5', children: [
                      { type: 'h3', className: 'text-base font-semibold mb-1', content: 'Chanderi Silk', style: { color: 'var(--color-text)' } },
                      { type: 'p', className: 'text-lg font-bold mb-3', content: '\u20B92,499', style: { color: 'var(--color-primary)' } },
                      { type: 'div', className: 'text-xs space-y-2', style: { color: 'var(--color-text-secondary)' }, children: [
                        { type: 'p', className: 'py-2 border-t', content: 'Fabric: Pure Silk', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Wash: Dry Clean', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Fit: Regular', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Occasion: Festive', style: { borderColor: 'var(--color-border)' } }
                      ]},
                      { type: 'a', className: 'mt-4 inline-block w-full py-2.5 rounded-lg text-sm font-semibold text-white text-center', content: 'Add to Cart', attributes: { href: '#' }, style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'border-2 rounded-2xl overflow-hidden text-center relative', style: { borderColor: 'var(--color-primary, #6366f1)' }, children: [
                    { type: 'span', className: 'absolute top-0 left-0 right-0 py-1 text-xs font-bold text-white text-center', content: 'MOST POPULAR', style: { backgroundColor: 'var(--color-primary, #6366f1)' } },
                    { type: 'div', className: 'aspect-square bg-gray-100 mt-6', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product B', loading: 'lazy' } }
                    ]},
                    { type: 'div', className: 'p-5', children: [
                      { type: 'h3', className: 'text-base font-semibold mb-1', content: 'Banarasi Weave', style: { color: 'var(--color-text)' } },
                      { type: 'p', className: 'text-lg font-bold mb-3', content: '\u20B93,999', style: { color: 'var(--color-primary)' } },
                      { type: 'div', className: 'text-xs space-y-2', style: { color: 'var(--color-text-secondary)' }, children: [
                        { type: 'p', className: 'py-2 border-t', content: 'Fabric: Banarasi Silk', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Wash: Dry Clean', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Fit: Slim', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Occasion: Wedding', style: { borderColor: 'var(--color-border)' } }
                      ]},
                      { type: 'a', className: 'mt-4 inline-block w-full py-2.5 rounded-lg text-sm font-semibold text-white text-center', content: 'Add to Cart', attributes: { href: '#' }, style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'border rounded-2xl overflow-hidden text-center', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'aspect-square bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Product C', loading: 'lazy' } }
                    ]},
                    { type: 'div', className: 'p-5', children: [
                      { type: 'h3', className: 'text-base font-semibold mb-1', content: 'Cotton Khadi', style: { color: 'var(--color-text)' } },
                      { type: 'p', className: 'text-lg font-bold mb-3', content: '\u20B91,499', style: { color: 'var(--color-primary)' } },
                      { type: 'div', className: 'text-xs space-y-2', style: { color: 'var(--color-text-secondary)' }, children: [
                        { type: 'p', className: 'py-2 border-t', content: 'Fabric: Khadi Cotton', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Wash: Machine Wash', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Fit: Relaxed', style: { borderColor: 'var(--color-border)' } },
                        { type: 'p', className: 'py-2 border-t', content: 'Occasion: Casual', style: { borderColor: 'var(--color-border)' } }
                      ]},
                      { type: 'a', className: 'mt-4 inline-block w-full py-2.5 rounded-lg text-sm font-semibold text-white text-center', content: 'Add to Cart', attributes: { href: '#' }, style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// HERO SECTIONS (5)
// ═══════════════════════════════════════════════════════════════════════

const heroSections = [

  // 8. Hero — Video Background
  {
    category: 'Hero',
    label: 'Hero — Video Background',
    description: 'Full-screen video background with gradient overlay, heading & CTA',
    icon: 'Play',
    block: {
      id: 'hero_video_bg',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'relative min-h-screen flex items-center justify-center overflow-hidden',
        children: [
          {
            type: 'div',
            className: 'absolute inset-0',
            children: [
              { type: 'div', className: 'absolute inset-0 bg-black/50 z-10', children: [] },
              { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=1200', alt: 'Hero background' }, style: { filter: 'brightness(0.7)' } }
            ]
          },
          {
            type: 'div',
            className: 'relative z-20 text-center px-4 md:px-8 max-w-4xl mx-auto',
            children: [
              { type: 'p', className: 'text-xs md:text-sm uppercase tracking-[0.3em] text-white/70 mb-4 md:mb-6 font-medium', content: '{{content.subtitle || "New Season Collection"}}' },
              { type: 'h1', className: 'text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight', content: '{{content.heading || "Redefine Your Style"}}', style: { fontFamily: 'var(--font-heading)' } },
              { type: 'p', className: 'text-base md:text-xl text-white/80 mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed', content: '{{content.description || "Discover our latest collection of handcrafted pieces that celebrate Indian artistry and modern elegance."}}' },
              { type: 'div', className: 'flex flex-col sm:flex-row gap-4 justify-center', children: [
                { type: 'a', className: 'px-8 py-3.5 rounded-xl text-base font-semibold text-center transition-opacity hover:opacity-90', content: 'Shop Collection', attributes: { href: '/shop' }, style: { backgroundColor: '#ffffff', color: '#111827' } },
                { type: 'a', className: 'px-8 py-3.5 rounded-xl text-base font-semibold text-center border-2 border-white/30 text-white hover:bg-white/10 transition-colors', content: 'Watch Video', attributes: { href: '#' } }
              ]}
            ]
          }
        ]
      }
    }
  },

  // 9. Hero — Carousel 3-slide
  {
    category: 'Hero',
    label: 'Hero — 3-Slide Carousel',
    description: 'Auto-play hero carousel with 3 slides, dots navigation',
    icon: 'Layers',
    block: {
      id: 'hero_carousel_3',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'relative min-h-[80vh] md:min-h-screen overflow-hidden',
        children: [
          {
            type: 'div',
            className: 'absolute inset-0',
            children: [
              { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=1200', alt: 'Slide 1' } },
              { type: 'div', className: 'absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent', children: [] }
            ]
          },
          {
            type: 'div',
            className: 'relative z-10 flex items-center min-h-[80vh] md:min-h-screen px-6 md:px-16 lg:px-24',
            children: [
              { type: 'div', className: 'max-w-xl', children: [
                { type: 'span', className: 'inline-block text-xs md:text-sm uppercase tracking-wider text-white/80 mb-4 font-medium border border-white/30 px-3 py-1 rounded-full', content: 'Slide 1 of 3' },
                { type: 'h1', className: 'text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight', content: '{{content.slide1Title || "Festive Collection 2026"}}', style: { fontFamily: 'var(--font-heading)' } },
                { type: 'p', className: 'text-sm md:text-lg text-white/80 mb-8 leading-relaxed', content: '{{content.slide1Text || "Celebrate every occasion with our exquisite range of handcrafted ethnic wear."}}' },
                { type: 'a', className: 'inline-block px-8 py-3 rounded-xl text-sm md:text-base font-semibold transition-opacity hover:opacity-90', content: 'Explore Now', attributes: { href: '/shop' }, style: { backgroundColor: '#ffffff', color: '#111827' } }
              ]}
            ]
          },
          {
            type: 'div',
            className: 'absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3',
            children: [
              { type: 'div', className: 'w-8 h-1.5 rounded-full bg-white', children: [] },
              { type: 'div', className: 'w-8 h-1.5 rounded-full bg-white/40', children: [] },
              { type: 'div', className: 'w-8 h-1.5 rounded-full bg-white/40', children: [] }
            ]
          }
        ]
      }
    }
  },

  // 10. Hero — Countdown
  {
    category: 'Hero',
    label: 'Hero — Launch Countdown',
    description: 'Hero section with countdown timer for product launch or sale',
    icon: 'Clock',
    block: {
      id: 'hero_countdown',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center',
        style: { background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)' },
        children: [
          {
            type: 'div',
            className: 'text-center px-4 md:px-8 max-w-3xl mx-auto',
            children: [
              { type: 'span', className: 'inline-block text-xs uppercase tracking-[0.3em] text-indigo-400 mb-6 font-semibold', content: '{{content.label || "Coming Soon"}}' },
              { type: 'h1', className: 'text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight', content: '{{content.heading || "Summer Collection 2026"}}', style: { fontFamily: 'var(--font-heading)' } },
              { type: 'p', className: 'text-base md:text-lg text-gray-300 mb-10 max-w-xl mx-auto', content: '{{content.description || "Something extraordinary is on its way. Be the first to explore our newest designs."}}' },
              {
                type: 'div',
                className: 'flex justify-center gap-4 md:gap-6 mb-10',
                children: [
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20', children: [
                      { type: 'span', className: 'text-2xl md:text-3xl font-bold text-white', content: '12' }
                    ]},
                    { type: 'span', className: 'text-[10px] md:text-xs uppercase tracking-wider text-gray-400 mt-2 block', content: 'Days' }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20', children: [
                      { type: 'span', className: 'text-2xl md:text-3xl font-bold text-white', content: '08' }
                    ]},
                    { type: 'span', className: 'text-[10px] md:text-xs uppercase tracking-wider text-gray-400 mt-2 block', content: 'Hours' }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20', children: [
                      { type: 'span', className: 'text-2xl md:text-3xl font-bold text-white', content: '45' }
                    ]},
                    { type: 'span', className: 'text-[10px] md:text-xs uppercase tracking-wider text-gray-400 mt-2 block', content: 'Minutes' }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20', children: [
                      { type: 'span', className: 'text-2xl md:text-3xl font-bold text-white', content: '30' }
                    ]},
                    { type: 'span', className: 'text-[10px] md:text-xs uppercase tracking-wider text-gray-400 mt-2 block', content: 'Seconds' }
                  ]}
                ]
              },
              { type: 'a', className: 'inline-block px-8 py-3.5 rounded-xl text-base font-semibold transition-all hover:scale-105', content: 'Notify Me', attributes: { href: '#' }, style: { backgroundColor: '#6366f1', color: '#ffffff' } }
            ]
          }
        ]
      }
    }
  },

  // 11. Hero — Minimal Text
  {
    category: 'Hero',
    label: 'Hero — Minimal Text',
    description: 'Clean text-only hero with accent line, very minimal',
    icon: 'Type',
    block: {
      id: 'hero_minimal_text',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'min-h-[70vh] md:min-h-[80vh] flex items-center justify-center px-6 md:px-12',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-3xl text-center',
            children: [
              { type: 'div', className: 'w-12 h-0.5 mx-auto mb-8', style: { backgroundColor: 'var(--color-primary, #111827)' }, children: [] },
              { type: 'h1', className: 'text-3xl md:text-5xl lg:text-7xl font-bold leading-tight mb-6', content: '{{content.heading || "Less is More"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text, #111827)', letterSpacing: '-0.03em' } },
              { type: 'p', className: 'text-base md:text-xl leading-relaxed mb-10 max-w-xl mx-auto', content: '{{content.description || "Timeless designs that speak through simplicity. Each piece is a statement in restraint and elegance."}}', style: { color: 'var(--color-text-secondary, #6b7280)' } },
              { type: 'a', className: 'inline-block text-sm md:text-base font-medium pb-1 border-b-2 transition-colors', content: 'Discover the Collection \u2192', attributes: { href: '/shop' }, style: { color: 'var(--color-text, #111827)', borderColor: 'var(--color-primary, #111827)' } }
            ]
          }
        ]
      }
    }
  },

  // 12. Hero — Fullscreen Image
  {
    category: 'Hero',
    label: 'Hero — Fullscreen Image',
    description: 'Full-screen image with centered text overlay and gradient',
    icon: 'Image',
    block: {
      id: 'hero_fullscreen_image',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'relative min-h-screen flex items-center justify-center',
        children: [
          { type: 'img', className: 'absolute inset-0 w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=1200', alt: 'Hero image' } },
          { type: 'div', className: 'absolute inset-0 bg-black/40', children: [] },
          {
            type: 'div',
            className: 'relative z-10 text-center px-6 md:px-12 max-w-4xl mx-auto',
            children: [
              { type: 'h1', className: 'text-4xl md:text-6xl lg:text-8xl font-bold text-white mb-6 leading-none', content: '{{content.heading || "Elegance Speaks"}}', style: { fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' } },
              { type: 'p', className: 'text-sm md:text-lg text-white/80 mb-8 max-w-lg mx-auto', content: '{{content.subheading || "Where tradition meets contemporary design"}}' },
              { type: 'a', className: 'inline-block px-10 py-4 rounded-full text-sm md:text-base font-semibold tracking-wide uppercase hover:opacity-90 transition-opacity', content: 'Shop Now', attributes: { href: '/shop' }, style: { backgroundColor: '#ffffff', color: '#111827', letterSpacing: '0.1em' } }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// CONTENT SECTIONS (8)
// ═══════════════════════════════════════════════════════════════════════

const contentSections = [

  // 13. Brand Story
  {
    category: 'Content',
    label: 'Brand Story',
    description: 'Image + narrative text split layout with decorative accent',
    icon: 'BookOpen',
    block: {
      id: 'brand_story',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-24 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center',
            children: [
              {
                type: 'div',
                className: 'relative',
                children: [
                  { type: 'img', className: 'w-full rounded-2xl', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Our Story', loading: 'lazy' } },
                  { type: 'div', className: 'absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl -z-10', style: { backgroundColor: 'var(--color-primary, #6366f1)', opacity: '0.1' }, children: [] }
                ]
              },
              {
                type: 'div',
                children: [
                  { type: 'span', className: 'text-xs uppercase tracking-[0.2em] font-semibold mb-4 block', content: '{{content.label || "Our Story"}}', style: { color: 'var(--color-primary)', letterSpacing: '0.2em' } },
                  { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold mb-6 leading-tight', content: '{{content.heading || "Crafted with Passion, Worn with Pride"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
                  { type: 'p', className: 'text-base md:text-lg leading-relaxed mb-4', content: '{{content.paragraph1 || "Born from a love for Indian textile heritage, our journey began in a small studio in Jaipur. What started as a passion project has blossomed into a brand that celebrates the beauty of handcrafted fashion."}}', style: { color: 'var(--color-text-secondary)' } },
                  { type: 'p', className: 'text-base md:text-lg leading-relaxed mb-6', content: '{{content.paragraph2 || "Every piece in our collection is a tribute to the master artisans who keep ancient weaving and embroidery traditions alive, while our designers infuse each creation with modern sensibility."}}', style: { color: 'var(--color-text-secondary)' } },
                  { type: 'div', className: 'flex items-center gap-4', children: [
                    { type: 'div', className: 'w-12 h-0.5', style: { backgroundColor: 'var(--color-primary, #111827)' }, children: [] },
                    { type: 'span', className: 'text-sm font-medium', content: 'Since 2020', style: { color: 'var(--color-text-secondary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 14. Announcement Bar
  {
    category: 'Content',
    label: 'Announcement Bar',
    description: 'Sticky top banner with offer text and close button',
    icon: 'Megaphone',
    block: {
      id: 'announcement_bar',
      type: 'section',
      position: 0,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'div',
        className: 'w-full py-2.5 px-4 text-center relative',
        style: { backgroundColor: 'var(--color-primary, #111827)' },
        children: [
          {
            type: 'div',
            className: 'flex items-center justify-center gap-2 flex-wrap',
            children: [
              { type: 'span', className: 'text-xs md:text-sm font-medium text-white', content: '{{content.text || "Free Shipping on orders above \u20B9999"}}' },
              { type: 'span', className: 'text-white/40 hidden md:inline', content: '|' },
              { type: 'a', className: 'text-xs md:text-sm font-semibold text-white underline underline-offset-2 hover:text-white/80', content: '{{content.linkText || "Shop Now"}}', attributes: { href: '/shop' } }
            ]
          },
          { type: 'span', className: 'absolute right-3 top-1/2 -translate-y-1/2 text-white/50 cursor-pointer hover:text-white text-lg leading-none', content: '\u00D7' }
        ]
      }
    }
  },

  // 15. Trust Badges
  {
    category: 'Content',
    label: 'Trust Badges',
    description: '4 trust icons: Free Shipping, COD, Easy Returns, Secure Payment',
    icon: 'ShieldCheck',
    block: {
      id: 'trust_badges',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-8 px-4 md:py-12 md:px-8 border-y',
        style: { backgroundColor: 'var(--color-surface, #fafafa)', borderColor: 'var(--color-border, #e5e7eb)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8',
            children: [
              { type: 'div', className: 'flex flex-col items-center text-center gap-2', children: [
                { type: 'div', className: 'w-12 h-12 rounded-full flex items-center justify-center text-xl', style: { backgroundColor: 'var(--color-primary, #111827)', color: '#ffffff', opacity: '0.1' }, children: [
                  { type: 'span', className: 'text-2xl', content: '\uD83D\uDE9A', style: { opacity: '1' } }
                ]},
                { type: 'span', className: 'text-2xl mb-1', content: '\uD83D\uDE9A' },
                { type: 'h3', className: 'text-sm font-semibold', content: 'Free Shipping', style: { color: 'var(--color-text)' } },
                { type: 'p', className: 'text-xs', content: 'On orders above \u20B9999', style: { color: 'var(--color-text-secondary)' } }
              ]},
              { type: 'div', className: 'flex flex-col items-center text-center gap-2', children: [
                { type: 'span', className: 'text-2xl mb-1', content: '\uD83D\uDCB5' },
                { type: 'h3', className: 'text-sm font-semibold', content: 'Cash on Delivery', style: { color: 'var(--color-text)' } },
                { type: 'p', className: 'text-xs', content: 'Pay when you receive', style: { color: 'var(--color-text-secondary)' } }
              ]},
              { type: 'div', className: 'flex flex-col items-center text-center gap-2', children: [
                { type: 'span', className: 'text-2xl mb-1', content: '\uD83D\uDD04' },
                { type: 'h3', className: 'text-sm font-semibold', content: 'Easy Returns', style: { color: 'var(--color-text)' } },
                { type: 'p', className: 'text-xs', content: '7-day return policy', style: { color: 'var(--color-text-secondary)' } }
              ]},
              { type: 'div', className: 'flex flex-col items-center text-center gap-2', children: [
                { type: 'span', className: 'text-2xl mb-1', content: '\uD83D\uDD12' },
                { type: 'h3', className: 'text-sm font-semibold', content: 'Secure Payments', style: { color: 'var(--color-text)' } },
                { type: 'p', className: 'text-xs', content: '100% safe checkout', style: { color: 'var(--color-text-secondary)' } }
              ]}
            ]
          }
        ]
      }
    }
  },

  // 16. Brand Logos
  {
    category: 'Content',
    label: 'Brand Logos — As Seen In',
    description: '"As Seen In" media/brand logos in a horizontal row',
    icon: 'Award',
    block: {
      id: 'brand_logos',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-10 px-4 md:py-16 md:px-8',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'p', className: 'text-center text-xs uppercase tracking-[0.2em] font-medium mb-8', content: '{{content.heading || "As Seen In"}}', style: { color: 'var(--color-text-secondary)', letterSpacing: '0.2em' } },
              {
                type: 'div',
                className: 'flex items-center justify-center flex-wrap gap-8 md:gap-16',
                children: [
                  { type: 'div', className: 'text-lg md:text-xl font-bold opacity-30 hover:opacity-60 transition-opacity cursor-pointer', content: 'VOGUE', style: { fontFamily: 'serif', color: 'var(--color-text)' } },
                  { type: 'div', className: 'text-lg md:text-xl font-bold opacity-30 hover:opacity-60 transition-opacity cursor-pointer', content: 'ELLE', style: { fontFamily: 'serif', color: 'var(--color-text)' } },
                  { type: 'div', className: 'text-lg md:text-xl font-bold opacity-30 hover:opacity-60 transition-opacity cursor-pointer', content: 'FEMINA', style: { fontFamily: 'serif', color: 'var(--color-text)' } },
                  { type: 'div', className: 'text-lg md:text-xl font-bold opacity-30 hover:opacity-60 transition-opacity cursor-pointer', content: 'GQ', style: { fontFamily: 'serif', color: 'var(--color-text)' } },
                  { type: 'div', className: 'text-lg md:text-xl font-bold opacity-30 hover:opacity-60 transition-opacity cursor-pointer', content: 'GRAZIA', style: { fontFamily: 'serif', color: 'var(--color-text)' } }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 17. Instagram Grid
  {
    category: 'Social',
    label: 'Instagram Grid',
    description: '6-image grid linked to Instagram with overlay icon',
    icon: 'Instagram',
    block: {
      id: 'instagram_grid',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'div', className: 'text-center mb-8', children: [
                { type: 'h2', className: 'text-2xl md:text-3xl font-bold mb-2', content: '{{content.heading || "Follow Us on Instagram"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
                { type: 'p', className: 'text-sm', content: '@yourbrandname', style: { color: 'var(--color-primary)' } }
              ]},
              {
                type: 'div',
                className: 'grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3',
                children: [
                  { type: 'div', className: 'relative group aspect-square overflow-hidden rounded-lg cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Instagram post', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center', children: [
                      { type: 'span', className: 'text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg', content: '\u2764' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group aspect-square overflow-hidden rounded-lg cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Instagram post', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center', children: [
                      { type: 'span', className: 'text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg', content: '\u2764' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group aspect-square overflow-hidden rounded-lg cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Instagram post', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center', children: [
                      { type: 'span', className: 'text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg', content: '\u2764' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group aspect-square overflow-hidden rounded-lg cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Instagram post', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center', children: [
                      { type: 'span', className: 'text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg', content: '\u2764' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group aspect-square overflow-hidden rounded-lg cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Instagram post', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center', children: [
                      { type: 'span', className: 'text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg', content: '\u2764' }
                    ]}
                  ]},
                  { type: 'div', className: 'relative group aspect-square overflow-hidden rounded-lg cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Instagram post', loading: 'lazy' } },
                    { type: 'div', className: 'absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center', children: [
                      { type: 'span', className: 'text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg', content: '\u2764' }
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 18. Lookbook Gallery
  {
    category: 'Content',
    label: 'Lookbook Gallery',
    description: '3-image editorial gallery with asymmetric layout',
    icon: 'Camera',
    block: {
      id: 'lookbook_gallery',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #f9fafb)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              { type: 'div', className: 'text-center mb-10', children: [
                { type: 'span', className: 'text-xs uppercase tracking-[0.2em] font-semibold mb-3 block', content: '{{content.label || "Lookbook"}}', style: { color: 'var(--color-primary)', letterSpacing: '0.2em' } },
                { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold', content: '{{content.heading || "Spring/Summer 2026"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } }
              ]},
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6',
                children: [
                  { type: 'div', className: 'md:col-span-2 md:row-span-2 overflow-hidden rounded-2xl group cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover min-h-[300px] md:min-h-[500px] group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Lookbook 1', loading: 'lazy' } }
                  ]},
                  { type: 'div', className: 'overflow-hidden rounded-2xl group cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover min-h-[200px] md:min-h-[240px] group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Lookbook 2', loading: 'lazy' } }
                  ]},
                  { type: 'div', className: 'overflow-hidden rounded-2xl group cursor-pointer', children: [
                    { type: 'img', className: 'w-full h-full object-cover min-h-[200px] md:min-h-[240px] group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=400', alt: 'Lookbook 3', loading: 'lazy' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 19. Process Timeline
  {
    category: 'Content',
    label: 'Process Timeline',
    description: '4-step process: Design, Craft, Quality, Deliver',
    icon: 'Compass',
    block: {
      id: 'process_timeline',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4', content: '{{content.heading || "How We Work"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm md:text-base text-center mb-12 max-w-xl mx-auto', content: '{{content.subheading || "From concept to your doorstep, every step is crafted with care"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4',
                children: [
                  { type: 'div', className: 'text-center relative', children: [
                    { type: 'div', className: 'w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-xl font-bold text-white', style: { backgroundColor: 'var(--color-primary, #111827)' }, children: [
                      { type: 'span', content: '01' }
                    ]},
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Design', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm leading-relaxed', content: 'Our designers sketch original patterns inspired by Indian heritage and global trends.', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'div', className: 'hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-gray-200', children: [] }
                  ]},
                  { type: 'div', className: 'text-center relative', children: [
                    { type: 'div', className: 'w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-xl font-bold text-white', style: { backgroundColor: 'var(--color-primary, #111827)' }, children: [
                      { type: 'span', content: '02' }
                    ]},
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Craft', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm leading-relaxed', content: 'Skilled artisans bring each design to life using traditional techniques and premium fabrics.', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'div', className: 'hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-gray-200', children: [] }
                  ]},
                  { type: 'div', className: 'text-center relative', children: [
                    { type: 'div', className: 'w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-xl font-bold text-white', style: { backgroundColor: 'var(--color-primary, #111827)' }, children: [
                      { type: 'span', content: '03' }
                    ]},
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Quality Check', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm leading-relaxed', content: 'Every piece passes through rigorous quality inspection before packaging.', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'div', className: 'hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-gray-200', children: [] }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-xl font-bold text-white', style: { backgroundColor: 'var(--color-primary, #111827)' }, children: [
                      { type: 'span', content: '04' }
                    ]},
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Deliver', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm leading-relaxed', content: 'Carefully packaged and delivered to your doorstep with love and care.', style: { color: 'var(--color-text-secondary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 20. Team Grid
  {
    category: 'Team',
    label: 'Team Grid',
    description: 'Team member cards with photo, name, and role',
    icon: 'Users',
    block: {
      id: 'team_grid',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #f9fafb)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3', content: '{{content.heading || "Meet Our Team"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm md:text-base text-center mb-10 max-w-lg mx-auto', content: '{{content.subheading || "The passionate people behind your favorite designs"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8',
                children: [
                  { type: 'div', className: 'text-center group', children: [
                    { type: 'div', className: 'aspect-square overflow-hidden rounded-2xl bg-gray-200 mb-4', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Team member', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'text-sm md:text-base font-semibold', content: 'Priya Sharma', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm', content: 'Founder & Designer', style: { color: 'var(--color-text-secondary)' } }
                  ]},
                  { type: 'div', className: 'text-center group', children: [
                    { type: 'div', className: 'aspect-square overflow-hidden rounded-2xl bg-gray-200 mb-4', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Team member', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'text-sm md:text-base font-semibold', content: 'Arjun Mehta', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm', content: 'Head of Operations', style: { color: 'var(--color-text-secondary)' } }
                  ]},
                  { type: 'div', className: 'text-center group', children: [
                    { type: 'div', className: 'aspect-square overflow-hidden rounded-2xl bg-gray-200 mb-4', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Team member', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'text-sm md:text-base font-semibold', content: 'Ananya Patel', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm', content: 'Creative Director', style: { color: 'var(--color-text-secondary)' } }
                  ]},
                  { type: 'div', className: 'text-center group', children: [
                    { type: 'div', className: 'aspect-square overflow-hidden rounded-2xl bg-gray-200 mb-4', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=300', alt: 'Team member', loading: 'lazy' } }
                    ]},
                    { type: 'h3', className: 'text-sm md:text-base font-semibold', content: 'Vikram Singh', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs md:text-sm', content: 'Master Artisan', style: { color: 'var(--color-text-secondary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// INTERACTIVE SECTIONS (6)
// ═══════════════════════════════════════════════════════════════════════

const interactiveSections = [

  // 21. FAQ Accordion
  {
    category: 'FAQ',
    label: 'FAQ Accordion',
    description: 'Expandable Q&A section with 5 common questions',
    icon: 'HelpCircle',
    block: {
      id: 'faq_accordion',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-3xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3', content: '{{content.heading || "Frequently Asked Questions"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm md:text-base text-center mb-10', content: '{{content.subheading || "Got questions? We have answers."}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'space-y-3',
                children: [
                  { type: 'div', className: 'border rounded-xl overflow-hidden', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-gray-50 transition-colors', children: [
                      { type: 'h3', className: 'text-sm md:text-base font-medium pr-4', content: 'What is the estimated delivery time?', style: { color: 'var(--color-text)' } },
                      { type: 'span', className: 'text-xl flex-shrink-0', content: '+', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'px-4 md:px-5 pb-4 md:pb-5', children: [
                      { type: 'p', className: 'text-sm leading-relaxed', content: 'We deliver across India within 5-7 business days. Express delivery (2-3 days) is available for metro cities at an additional charge.', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'border rounded-xl overflow-hidden', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-gray-50 transition-colors', children: [
                      { type: 'h3', className: 'text-sm md:text-base font-medium pr-4', content: 'What is your return policy?', style: { color: 'var(--color-text)' } },
                      { type: 'span', className: 'text-xl flex-shrink-0', content: '+', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'px-4 md:px-5 pb-4 md:pb-5', children: [
                      { type: 'p', className: 'text-sm leading-relaxed', content: 'We offer a 7-day easy return policy. Items must be unworn, unwashed, and in original packaging with tags attached.', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'border rounded-xl overflow-hidden', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-gray-50 transition-colors', children: [
                      { type: 'h3', className: 'text-sm md:text-base font-medium pr-4', content: 'Do you offer COD (Cash on Delivery)?', style: { color: 'var(--color-text)' } },
                      { type: 'span', className: 'text-xl flex-shrink-0', content: '+', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'px-4 md:px-5 pb-4 md:pb-5', children: [
                      { type: 'p', className: 'text-sm leading-relaxed', content: 'Yes! We offer Cash on Delivery across all serviceable pin codes in India. COD is available for orders up to \u20B910,000.', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'border rounded-xl overflow-hidden', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-gray-50 transition-colors', children: [
                      { type: 'h3', className: 'text-sm md:text-base font-medium pr-4', content: 'How do I find my correct size?', style: { color: 'var(--color-text)' } },
                      { type: 'span', className: 'text-xl flex-shrink-0', content: '+', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'px-4 md:px-5 pb-4 md:pb-5', children: [
                      { type: 'p', className: 'text-sm leading-relaxed', content: 'Each product page has a detailed size guide. You can also reach out to us on WhatsApp for personalized size assistance.', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'border rounded-xl overflow-hidden', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'div', className: 'flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-gray-50 transition-colors', children: [
                      { type: 'h3', className: 'text-sm md:text-base font-medium pr-4', content: 'Are your products handmade?', style: { color: 'var(--color-text)' } },
                      { type: 'span', className: 'text-xl flex-shrink-0', content: '+', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'px-4 md:px-5 pb-4 md:pb-5', children: [
                      { type: 'p', className: 'text-sm leading-relaxed', content: 'Yes, many of our products feature handcrafted elements like hand embroidery, block printing, and hand weaving by skilled artisans.', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 22. Contact Form + Info
  {
    category: 'Contact',
    label: 'Contact Form & Info',
    description: 'Contact form with address, phone, and email details',
    icon: 'Phone',
    block: {
      id: 'contact_form_map',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-10', content: '{{content.heading || "Get in Touch"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              {
                type: 'div',
                className: 'grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16',
                children: [
                  {
                    type: 'div',
                    className: 'space-y-6',
                    children: [
                      { type: 'div', children: [
                        { type: 'label', className: 'block text-sm font-medium mb-2', content: 'Full Name', style: { color: 'var(--color-text)' } },
                        { type: 'div', className: 'w-full h-11 rounded-lg border px-4 flex items-center text-sm', content: 'Enter your name', style: { borderColor: 'var(--color-border, #e5e7eb)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-surface, #f9fafb)' } }
                      ]},
                      { type: 'div', children: [
                        { type: 'label', className: 'block text-sm font-medium mb-2', content: 'Email Address', style: { color: 'var(--color-text)' } },
                        { type: 'div', className: 'w-full h-11 rounded-lg border px-4 flex items-center text-sm', content: 'you@example.com', style: { borderColor: 'var(--color-border, #e5e7eb)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-surface, #f9fafb)' } }
                      ]},
                      { type: 'div', children: [
                        { type: 'label', className: 'block text-sm font-medium mb-2', content: 'Message', style: { color: 'var(--color-text)' } },
                        { type: 'div', className: 'w-full h-32 rounded-lg border px-4 py-3 text-sm', content: 'Type your message here...', style: { borderColor: 'var(--color-border, #e5e7eb)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-surface, #f9fafb)' } }
                      ]},
                      { type: 'a', className: 'inline-block px-8 py-3 rounded-xl text-sm font-semibold text-white text-center', content: 'Send Message', attributes: { href: '#' }, style: { backgroundColor: 'var(--color-primary, #111827)' } }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'space-y-8 lg:pl-8',
                    children: [
                      { type: 'div', children: [
                        { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Visit Our Studio', style: { color: 'var(--color-text)' } },
                        { type: 'p', className: 'text-sm leading-relaxed', content: '123, Textile Market, Johari Bazaar,\nJaipur, Rajasthan 302001', style: { color: 'var(--color-text-secondary)' } }
                      ]},
                      { type: 'div', children: [
                        { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Call Us', style: { color: 'var(--color-text)' } },
                        { type: 'p', className: 'text-sm', content: '+91 98765 43210', style: { color: 'var(--color-text-secondary)' } },
                        { type: 'p', className: 'text-xs mt-1', content: 'Mon-Sat, 10AM - 7PM IST', style: { color: 'var(--color-text-secondary)' } }
                      ]},
                      { type: 'div', children: [
                        { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Email', style: { color: 'var(--color-text)' } },
                        { type: 'p', className: 'text-sm', content: 'hello@yourbrand.com', style: { color: 'var(--color-primary)' } }
                      ]},
                      { type: 'div', children: [
                        { type: 'h3', className: 'text-base font-semibold mb-2', content: 'WhatsApp', style: { color: 'var(--color-text)' } },
                        { type: 'a', className: 'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white', content: 'Chat on WhatsApp', attributes: { href: 'https://wa.me/919876543210' }, style: { backgroundColor: '#25D366' } }
                      ]}
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 23. Countdown Timer
  {
    category: 'CTA',
    label: 'Countdown Timer — Sale',
    description: 'Big countdown numbers for flash sale or limited offer',
    icon: 'Clock',
    block: {
      id: 'countdown_timer',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-16 md:px-8',
        style: { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto text-center',
            children: [
              { type: 'span', className: 'inline-block text-xs md:text-sm uppercase tracking-wider text-white/80 mb-3 font-medium', content: '{{content.label || "Flash Sale Ends In"}}' },
              { type: 'h2', className: 'text-2xl md:text-4xl font-bold text-white mb-8', content: '{{content.heading || "Up to 50% Off Everything"}}' },
              {
                type: 'div',
                className: 'flex justify-center gap-3 md:gap-6 mb-8',
                children: [
                  { type: 'div', className: 'bg-white/20 backdrop-blur-sm rounded-xl p-3 md:p-5 min-w-[70px] md:min-w-[90px]', children: [
                    { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white', content: '02' },
                    { type: 'div', className: 'text-[10px] md:text-xs uppercase text-white/70 mt-1', content: 'Days' }
                  ]},
                  { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white/60 self-center', content: ':' },
                  { type: 'div', className: 'bg-white/20 backdrop-blur-sm rounded-xl p-3 md:p-5 min-w-[70px] md:min-w-[90px]', children: [
                    { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white', content: '14' },
                    { type: 'div', className: 'text-[10px] md:text-xs uppercase text-white/70 mt-1', content: 'Hours' }
                  ]},
                  { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white/60 self-center', content: ':' },
                  { type: 'div', className: 'bg-white/20 backdrop-blur-sm rounded-xl p-3 md:p-5 min-w-[70px] md:min-w-[90px]', children: [
                    { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white', content: '38' },
                    { type: 'div', className: 'text-[10px] md:text-xs uppercase text-white/70 mt-1', content: 'Minutes' }
                  ]},
                  { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white/60 self-center', content: ':' },
                  { type: 'div', className: 'bg-white/20 backdrop-blur-sm rounded-xl p-3 md:p-5 min-w-[70px] md:min-w-[90px]', children: [
                    { type: 'div', className: 'text-3xl md:text-5xl font-bold text-white', content: '52' },
                    { type: 'div', className: 'text-[10px] md:text-xs uppercase text-white/70 mt-1', content: 'Seconds' }
                  ]}
                ]
              },
              { type: 'a', className: 'inline-block px-10 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity', content: 'Shop the Sale', attributes: { href: '/shop?sale=true' }, style: { backgroundColor: '#ffffff', color: '#dc2626' } }
            ]
          }
        ]
      }
    }
  },

  // 24. Tabs Content
  {
    category: 'Content',
    label: 'Tabs — Details / Reviews / Shipping',
    description: '3-tab content area for product info, reviews, and shipping',
    icon: 'SquareStack',
    block: {
      id: 'tabs_content',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-16 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto',
            children: [
              {
                type: 'div',
                className: 'flex border-b mb-8 gap-0',
                style: { borderColor: 'var(--color-border, #e5e7eb)' },
                children: [
                  { type: 'div', className: 'px-4 md:px-6 py-3 text-sm font-semibold border-b-2 cursor-pointer', content: 'Details', style: { borderColor: 'var(--color-primary, #111827)', color: 'var(--color-text)' } },
                  { type: 'div', className: 'px-4 md:px-6 py-3 text-sm font-medium border-b-2 border-transparent cursor-pointer', content: 'Reviews (24)', style: { color: 'var(--color-text-secondary)' } },
                  { type: 'div', className: 'px-4 md:px-6 py-3 text-sm font-medium border-b-2 border-transparent cursor-pointer', content: 'Shipping', style: { color: 'var(--color-text-secondary)' } }
                ]
              },
              {
                type: 'div',
                children: [
                  { type: 'h3', className: 'text-lg font-semibold mb-4', content: 'Product Details', style: { color: 'var(--color-text)' } },
                  { type: 'p', className: 'text-sm leading-relaxed mb-4', content: 'This exquisite piece is handcrafted by skilled artisans using traditional techniques passed down through generations. The fabric is sourced from the finest mills in India, ensuring superior quality and comfort.', style: { color: 'var(--color-text-secondary)' } },
                  { type: 'div', className: 'space-y-2 mt-6', children: [
                    { type: 'div', className: 'flex justify-between py-2 border-b text-sm', style: { borderColor: 'var(--color-border)' }, children: [
                      { type: 'span', className: 'font-medium', content: 'Fabric', style: { color: 'var(--color-text)' } },
                      { type: 'span', content: 'Pure Chanderi Silk', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'flex justify-between py-2 border-b text-sm', style: { borderColor: 'var(--color-border)' }, children: [
                      { type: 'span', className: 'font-medium', content: 'Work', style: { color: 'var(--color-text)' } },
                      { type: 'span', content: 'Zari Embroidery', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'flex justify-between py-2 border-b text-sm', style: { borderColor: 'var(--color-border)' }, children: [
                      { type: 'span', className: 'font-medium', content: 'Care', style: { color: 'var(--color-text)' } },
                      { type: 'span', content: 'Dry Clean Only', style: { color: 'var(--color-text-secondary)' } }
                    ]},
                    { type: 'div', className: 'flex justify-between py-2 text-sm', children: [
                      { type: 'span', className: 'font-medium', content: 'Origin', style: { color: 'var(--color-text)' } },
                      { type: 'span', content: 'Chanderi, Madhya Pradesh', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 25. Newsletter Offer
  {
    category: 'Contact',
    label: 'Newsletter — Get 10% Off',
    description: 'Email signup section with discount offer',
    icon: 'Gift',
    block: {
      id: 'newsletter_offer',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8',
        style: { backgroundColor: '#111827' },
        children: [
          {
            type: 'div',
            className: 'max-w-2xl mx-auto text-center',
            children: [
              { type: 'span', className: 'inline-block text-3xl md:text-4xl mb-4', content: '\uD83C\uDF81' },
              { type: 'h2', className: 'text-2xl md:text-3xl font-bold text-white mb-3', content: '{{content.heading || "Get 10% Off Your First Order"}}', style: { fontFamily: 'var(--font-heading)' } },
              { type: 'p', className: 'text-sm md:text-base text-gray-400 mb-8 max-w-md mx-auto', content: '{{content.description || "Subscribe to our newsletter for exclusive offers, new arrivals, and styling tips delivered to your inbox."}}' },
              {
                type: 'div',
                className: 'flex flex-col sm:flex-row gap-3 max-w-md mx-auto',
                children: [
                  { type: 'div', className: 'flex-1 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center px-4 text-sm text-white/50', content: 'Enter your email' },
                  { type: 'a', className: 'h-12 px-6 rounded-xl flex items-center justify-center text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap', content: 'Subscribe', attributes: { href: '#' }, style: { backgroundColor: 'var(--color-primary, #6366f1)' } }
                ]
              },
              { type: 'p', className: 'text-xs text-gray-500 mt-4', content: 'No spam, ever. Unsubscribe anytime.' }
            ]
          }
        ]
      }
    }
  },

  // 26. Size Guide
  {
    category: 'Content',
    label: 'Size Guide Table',
    description: 'Size chart table with S/M/L/XL measurements',
    icon: 'Ruler',
    block: {
      id: 'size_guide',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-16 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-3xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl font-bold text-center mb-3', content: '{{content.heading || "Size Guide"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm text-center mb-8', content: '{{content.subheading || "All measurements are in inches. Measure yourself for the perfect fit."}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'overflow-x-auto rounded-xl border',
                style: { borderColor: 'var(--color-border, #e5e7eb)' },
                children: [
                  {
                    type: 'div',
                    className: 'min-w-[500px]',
                    children: [
                      { type: 'div', className: 'grid grid-cols-5 text-sm font-semibold py-3 px-4', style: { backgroundColor: 'var(--color-primary, #111827)', color: '#ffffff' }, children: [
                        { type: 'span', content: 'Size' },
                        { type: 'span', content: 'Bust' },
                        { type: 'span', content: 'Waist' },
                        { type: 'span', content: 'Hip' },
                        { type: 'span', content: 'Length' }
                      ]},
                      { type: 'div', className: 'grid grid-cols-5 text-sm py-3 px-4 border-b', style: { borderColor: 'var(--color-border)', color: 'var(--color-text)' }, children: [
                        { type: 'span', className: 'font-medium', content: 'S' },
                        { type: 'span', content: '34' },
                        { type: 'span', content: '28' },
                        { type: 'span', content: '36' },
                        { type: 'span', content: '42' }
                      ]},
                      { type: 'div', className: 'grid grid-cols-5 text-sm py-3 px-4 border-b', style: { borderColor: 'var(--color-border)', color: 'var(--color-text)', backgroundColor: '#f9fafb' }, children: [
                        { type: 'span', className: 'font-medium', content: 'M' },
                        { type: 'span', content: '36' },
                        { type: 'span', content: '30' },
                        { type: 'span', content: '38' },
                        { type: 'span', content: '43' }
                      ]},
                      { type: 'div', className: 'grid grid-cols-5 text-sm py-3 px-4 border-b', style: { borderColor: 'var(--color-border)', color: 'var(--color-text)' }, children: [
                        { type: 'span', className: 'font-medium', content: 'L' },
                        { type: 'span', content: '38' },
                        { type: 'span', content: '32' },
                        { type: 'span', content: '40' },
                        { type: 'span', content: '44' }
                      ]},
                      { type: 'div', className: 'grid grid-cols-5 text-sm py-3 px-4 border-b', style: { borderColor: 'var(--color-border)', color: 'var(--color-text)', backgroundColor: '#f9fafb' }, children: [
                        { type: 'span', className: 'font-medium', content: 'XL' },
                        { type: 'span', content: '40' },
                        { type: 'span', content: '34' },
                        { type: 'span', content: '42' },
                        { type: 'span', content: '45' }
                      ]},
                      { type: 'div', className: 'grid grid-cols-5 text-sm py-3 px-4', style: { color: 'var(--color-text)' }, children: [
                        { type: 'span', className: 'font-medium', content: 'XXL' },
                        { type: 'span', content: '42' },
                        { type: 'span', content: '36' },
                        { type: 'span', content: '44' },
                        { type: 'span', content: '46' }
                      ]}
                    ]
                  }
                ]
              },
              { type: 'p', className: 'text-xs text-center mt-4', content: 'Need help? Contact us on WhatsApp for size assistance.', style: { color: 'var(--color-text-secondary)' } }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// SOCIAL / PROOF SECTIONS (5)
// ═══════════════════════════════════════════════════════════════════════

const socialProofSections = [

  // 27. Reviews Carousel
  {
    category: 'Testimonials',
    label: 'Customer Reviews Carousel',
    description: 'Customer review cards with star ratings and photos',
    icon: 'Star',
    block: {
      id: 'reviews_carousel',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8',
        style: { backgroundColor: 'var(--color-surface, #f9fafb)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3', content: '{{content.heading || "What Our Customers Say"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm md:text-base text-center mb-10', content: '{{content.subheading || "Trusted by 10,000+ happy customers across India"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-3 gap-6',
                children: [
                  { type: 'div', className: 'bg-white rounded-2xl p-6 shadow-sm border', style: { borderColor: 'var(--color-border, #f3f4f6)' }, children: [
                    { type: 'div', className: 'flex items-center gap-1 mb-3', children: [
                      { type: 'span', className: 'text-sm', content: '\u2605\u2605\u2605\u2605\u2605', style: { color: '#f59e0b' } }
                    ]},
                    { type: 'p', className: 'text-sm leading-relaxed mb-4', content: '"The quality of the silk saree was beyond my expectations. The embroidery work is so detailed and beautiful. Received it well packaged within 4 days!"', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'div', className: 'flex items-center gap-3', children: [
                      { type: 'div', className: 'w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-semibold', content: 'SK', style: { color: 'var(--color-primary)' } },
                      { type: 'div', children: [
                        { type: 'p', className: 'text-sm font-medium', content: 'Sneha Kapoor', style: { color: 'var(--color-text)' } },
                        { type: 'p', className: 'text-xs', content: 'Mumbai', style: { color: 'var(--color-text-secondary)' } }
                      ]}
                    ]}
                  ]},
                  { type: 'div', className: 'bg-white rounded-2xl p-6 shadow-sm border', style: { borderColor: 'var(--color-border, #f3f4f6)' }, children: [
                    { type: 'div', className: 'flex items-center gap-1 mb-3', children: [
                      { type: 'span', className: 'text-sm', content: '\u2605\u2605\u2605\u2605\u2605', style: { color: '#f59e0b' } }
                    ]},
                    { type: 'p', className: 'text-sm leading-relaxed mb-4', content: '"Ordered a kurta set for my wedding functions. The fabric quality is premium and the fit was perfect. The team was very helpful with size suggestions on WhatsApp."', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'div', className: 'flex items-center gap-3', children: [
                      { type: 'div', className: 'w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-sm font-semibold', content: 'RG', style: { color: '#d97706' } },
                      { type: 'div', children: [
                        { type: 'p', className: 'text-sm font-medium', content: 'Ritu Gupta', style: { color: 'var(--color-text)' } },
                        { type: 'p', className: 'text-xs', content: 'Delhi', style: { color: 'var(--color-text-secondary)' } }
                      ]}
                    ]}
                  ]},
                  { type: 'div', className: 'bg-white rounded-2xl p-6 shadow-sm border', style: { borderColor: 'var(--color-border, #f3f4f6)' }, children: [
                    { type: 'div', className: 'flex items-center gap-1 mb-3', children: [
                      { type: 'span', className: 'text-sm', content: '\u2605\u2605\u2605\u2605\u2606', style: { color: '#f59e0b' } }
                    ]},
                    { type: 'p', className: 'text-sm leading-relaxed mb-4', content: '"Love the Ikat print palazzo! The cotton quality is great for Indian summers. Already placed my second order. Returns were also very smooth when I needed a size exchange."', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'div', className: 'flex items-center gap-3', children: [
                      { type: 'div', className: 'w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-semibold', content: 'PM', style: { color: '#059669' } },
                      { type: 'div', children: [
                        { type: 'p', className: 'text-sm font-medium', content: 'Priya Menon', style: { color: 'var(--color-text)' } },
                        { type: 'p', className: 'text-xs', content: 'Bangalore', style: { color: 'var(--color-text-secondary)' } }
                      ]}
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 28. Video Testimonial
  {
    category: 'Testimonials',
    label: 'Video Testimonial',
    description: 'Video embed placeholder with customer quote beside it',
    icon: 'Video',
    block: {
      id: 'video_testimonial',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center',
            children: [
              {
                type: 'div',
                className: 'aspect-video rounded-2xl overflow-hidden bg-gray-900 relative flex items-center justify-center',
                children: [
                  { type: 'img', className: 'w-full h-full object-cover opacity-60', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Video thumbnail', loading: 'lazy' } },
                  { type: 'div', className: 'absolute inset-0 flex items-center justify-center', children: [
                    { type: 'div', className: 'w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform', children: [
                      { type: 'span', className: 'text-2xl ml-1', content: '\u25B6', style: { color: 'var(--color-primary, #111827)' } }
                    ]}
                  ]}
                ]
              },
              {
                type: 'div',
                children: [
                  { type: 'span', className: 'text-4xl md:text-5xl leading-none opacity-10 font-serif', content: '\u201C', style: { color: 'var(--color-primary)' } },
                  { type: 'p', className: 'text-lg md:text-xl leading-relaxed mb-6 -mt-4', content: '"I have been a loyal customer for over a year now. The quality, the designs, the packaging -- everything is top-notch. It truly feels like wearing art."', style: { color: 'var(--color-text)', fontStyle: 'italic' } },
                  { type: 'div', className: 'flex items-center gap-4', children: [
                    { type: 'div', className: 'w-12 h-12 rounded-full bg-gray-200 overflow-hidden', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100', alt: 'Customer', loading: 'lazy' } }
                    ]},
                    { type: 'div', children: [
                      { type: 'p', className: 'text-base font-semibold', content: 'Meera Joshi', style: { color: 'var(--color-text)' } },
                      { type: 'p', className: 'text-sm', content: 'Fashion Blogger, Pune', style: { color: 'var(--color-text-secondary)' } }
                    ]}
                  ]},
                  { type: 'div', className: 'flex items-center gap-1 mt-3', children: [
                    { type: 'span', className: 'text-sm', content: '\u2605\u2605\u2605\u2605\u2605', style: { color: '#f59e0b' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 29. Social Links Bar
  {
    category: 'Social',
    label: 'Social Media Links Bar',
    description: 'Horizontal bar with social media platform icons',
    icon: 'Share2',
    block: {
      id: 'social_links_bar',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-8 px-4 md:py-12 md:px-8',
        style: { backgroundColor: 'var(--color-surface, #f9fafb)' },
        children: [
          {
            type: 'div',
            className: 'max-w-2xl mx-auto text-center',
            children: [
              { type: 'p', className: 'text-sm font-medium mb-6', content: '{{content.heading || "Follow us for daily inspiration"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'flex items-center justify-center gap-4 md:gap-6',
                children: [
                  { type: 'a', className: 'w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold transition-transform hover:scale-110', content: 'IG', attributes: { href: '#', title: 'Instagram' }, style: { background: 'linear-gradient(135deg, #833AB4, #FD1D1D, #FCB045)', color: '#ffffff' } },
                  { type: 'a', className: 'w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold transition-transform hover:scale-110', content: 'FB', attributes: { href: '#', title: 'Facebook' }, style: { backgroundColor: '#1877F2', color: '#ffffff' } },
                  { type: 'a', className: 'w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold transition-transform hover:scale-110', content: 'YT', attributes: { href: '#', title: 'YouTube' }, style: { backgroundColor: '#FF0000', color: '#ffffff' } },
                  { type: 'a', className: 'w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold transition-transform hover:scale-110', content: 'TW', attributes: { href: '#', title: 'Twitter' }, style: { backgroundColor: '#1DA1F2', color: '#ffffff' } },
                  { type: 'a', className: 'w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold transition-transform hover:scale-110', content: 'PI', attributes: { href: '#', title: 'Pinterest' }, style: { backgroundColor: '#E60023', color: '#ffffff' } }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 30. Stats Animated
  {
    category: 'Stats',
    label: 'Stats — Big Numbers',
    description: '4 animated stat counters: Products, Customers, Cities, Rating',
    icon: 'BarChart3',
    block: {
      id: 'stats_animated',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8',
        style: { backgroundColor: 'var(--color-primary, #111827)' },
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl font-bold text-center text-white mb-12', content: '{{content.heading || "Our Impact in Numbers"}}', style: { fontFamily: 'var(--font-heading)' } },
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12',
                children: [
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2', content: '500+', style: { letterSpacing: '-0.02em' } },
                    { type: 'p', className: 'text-sm text-white/60', content: 'Products' }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2', content: '10K+', style: { letterSpacing: '-0.02em' } },
                    { type: 'p', className: 'text-sm text-white/60', content: 'Happy Customers' }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2', content: '100+', style: { letterSpacing: '-0.02em' } },
                    { type: 'p', className: 'text-sm text-white/60', content: 'Cities Served' }
                  ]},
                  { type: 'div', className: 'text-center', children: [
                    { type: 'div', className: 'text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2', content: '4.9', style: { letterSpacing: '-0.02em' } },
                    { type: 'p', className: 'text-sm text-white/60', content: 'Average Rating' }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 31. Collections Grid
  {
    category: 'Products',
    label: 'Collections Grid',
    description: '3-column collection cards with image, title & "Shop Now"',
    icon: 'Grid',
    block: {
      id: 'collections_grid',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-20 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3', content: '{{content.heading || "Shop Our Collections"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm md:text-base text-center mb-10 max-w-lg mx-auto', content: '{{content.subheading || "Curated collections for every occasion and mood"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-3 gap-6',
                children: [
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100 relative', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=500', alt: 'Festive Collection', loading: 'lazy' } },
                      { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent', children: [] },
                      { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-6', children: [
                        { type: 'h3', className: 'text-xl md:text-2xl font-bold text-white mb-2', content: 'Festive Collection' },
                        { type: 'span', className: 'inline-block text-sm font-medium text-white border-b border-white/50 pb-0.5 group-hover:border-white transition-colors', content: 'Shop Now \u2192' }
                      ]}
                    ]}
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100 relative', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=500', alt: 'Everyday Essentials', loading: 'lazy' } },
                      { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent', children: [] },
                      { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-6', children: [
                        { type: 'h3', className: 'text-xl md:text-2xl font-bold text-white mb-2', content: 'Everyday Essentials' },
                        { type: 'span', className: 'inline-block text-sm font-medium text-white border-b border-white/50 pb-0.5 group-hover:border-white transition-colors', content: 'Shop Now \u2192' }
                      ]}
                    ]}
                  ]},
                  { type: 'div', className: 'group cursor-pointer', children: [
                    { type: 'div', className: 'aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100 relative', children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=500', alt: 'Bridal Edit', loading: 'lazy' } },
                      { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent', children: [] },
                      { type: 'div', className: 'absolute bottom-0 left-0 right-0 p-6', children: [
                        { type: 'h3', className: 'text-xl md:text-2xl font-bold text-white mb-2', content: 'Bridal Edit' },
                        { type: 'span', className: 'inline-block text-sm font-medium text-white border-b border-white/50 pb-0.5 group-hover:border-white transition-colors', content: 'Shop Now \u2192' }
                      ]}
                    ]}
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// E-COMMERCE SECTIONS (4)
// ═══════════════════════════════════════════════════════════════════════

const ecommerceSections = [

  // 32. Sale Banner
  {
    category: 'CTA',
    label: 'Sale Banner — Full Width',
    description: 'Bold full-width sale banner with discount percentage and CTA',
    icon: 'Percent',
    block: {
      id: 'sale_banner',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'relative py-16 px-4 md:py-24 md:px-8 overflow-hidden',
        style: { background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%)' },
        children: [
          { type: 'div', className: 'absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10', style: { backgroundColor: '#ffffff' }, children: [] },
          { type: 'div', className: 'absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10', style: { backgroundColor: '#ffffff' }, children: [] },
          {
            type: 'div',
            className: 'relative z-10 max-w-4xl mx-auto text-center',
            children: [
              { type: 'span', className: 'inline-block text-xs md:text-sm uppercase tracking-wider font-semibold text-white/80 mb-4', content: '{{content.label || "Limited Time Offer"}}' },
              { type: 'div', className: 'text-6xl md:text-8xl lg:text-9xl font-black text-white mb-4 leading-none', content: '{{content.discount || "50%"}}', style: { letterSpacing: '-0.05em' } },
              { type: 'h2', className: 'text-2xl md:text-4xl font-bold text-white mb-6', content: '{{content.heading || "End of Season Sale"}}' },
              { type: 'p', className: 'text-sm md:text-base text-white/80 mb-8 max-w-md mx-auto', content: '{{content.description || "Save big on your favourite styles. Use code SALE50 at checkout."}}' },
              { type: 'a', className: 'inline-block px-10 py-4 rounded-xl text-base font-bold hover:opacity-90 transition-all hover:scale-105', content: 'Shop the Sale', attributes: { href: '/shop?sale=true' }, style: { backgroundColor: '#ffffff', color: '#4f46e5' } }
            ]
          }
        ]
      }
    }
  },

  // 33. Shipping Info
  {
    category: 'Content',
    label: 'Shipping Info Cards',
    description: '3 shipping option cards: Standard, Express, Same Day',
    icon: 'Truck',
    block: {
      id: 'shipping_info',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 px-4 md:py-16 md:px-8 lg:px-16',
        style: { backgroundColor: 'var(--color-surface, #ffffff)' },
        children: [
          {
            type: 'div',
            className: 'max-w-5xl mx-auto',
            children: [
              { type: 'h2', className: 'text-2xl md:text-3xl font-bold text-center mb-3', content: '{{content.heading || "Shipping & Delivery"}}', style: { fontFamily: 'var(--font-heading)', color: 'var(--color-text)' } },
              { type: 'p', className: 'text-sm text-center mb-10', content: '{{content.subheading || "We deliver across India with care and speed"}}', style: { color: 'var(--color-text-secondary)' } },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-3 gap-6',
                children: [
                  { type: 'div', className: 'border rounded-2xl p-6 text-center hover:shadow-md transition-shadow', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'span', className: 'text-3xl mb-4 block', content: '\uD83D\uDCE6' },
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Standard Delivery', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-2xl font-bold mb-1', content: 'FREE', style: { color: 'var(--color-primary)' } },
                    { type: 'p', className: 'text-sm mb-3', content: '5-7 Business Days', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'p', className: 'text-xs', content: 'On all orders above \u20B9999', style: { color: 'var(--color-text-secondary)' } }
                  ]},
                  { type: 'div', className: 'border-2 rounded-2xl p-6 text-center relative hover:shadow-md transition-shadow', style: { borderColor: 'var(--color-primary, #6366f1)' }, children: [
                    { type: 'span', className: 'absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase px-3 py-1 rounded-full text-white', content: 'POPULAR', style: { backgroundColor: 'var(--color-primary, #6366f1)' } },
                    { type: 'span', className: 'text-3xl mb-4 block mt-2', content: '\uD83D\uDE9A' },
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Express Delivery', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-2xl font-bold mb-1', content: '\u20B9149', style: { color: 'var(--color-primary)' } },
                    { type: 'p', className: 'text-sm mb-3', content: '2-3 Business Days', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'p', className: 'text-xs', content: 'Available in metro cities', style: { color: 'var(--color-text-secondary)' } }
                  ]},
                  { type: 'div', className: 'border rounded-2xl p-6 text-center hover:shadow-md transition-shadow', style: { borderColor: 'var(--color-border, #e5e7eb)' }, children: [
                    { type: 'span', className: 'text-3xl mb-4 block', content: '\u26A1' },
                    { type: 'h3', className: 'text-base font-semibold mb-2', content: 'Same Day Delivery', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-2xl font-bold mb-1', content: '\u20B9299', style: { color: 'var(--color-primary)' } },
                    { type: 'p', className: 'text-sm mb-3', content: 'Order by 12 PM', style: { color: 'var(--color-text-secondary)' } },
                    { type: 'p', className: 'text-xs', content: 'Select cities only', style: { color: 'var(--color-text-secondary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 34. Recently Viewed
  {
    category: 'Products',
    label: 'Recently Viewed Products',
    description: 'Horizontal scroll of recently viewed product cards',
    icon: 'Clock',
    block: {
      id: 'recently_viewed',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-10 px-4 md:py-14 md:px-8 border-t',
        style: { backgroundColor: 'var(--color-surface, #fafafa)', borderColor: 'var(--color-border, #e5e7eb)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto',
            children: [
              { type: 'h2', className: 'text-lg md:text-xl font-semibold mb-6', content: '{{content.heading || "Recently Viewed"}}', style: { color: 'var(--color-text)' } },
              {
                type: 'div',
                className: 'flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory',
                style: { scrollbarWidth: 'none' },
                children: [
                  { type: 'div', className: 'flex-shrink-0 w-[140px] md:w-[180px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=200', alt: 'Recently viewed', loading: 'lazy' } }
                    ]},
                    { type: 'p', className: 'text-xs mt-2 truncate', content: 'Chanderi Kurta', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs font-semibold', content: '\u20B92,499', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[140px] md:w-[180px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6567339/pexels-photo-6567339.jpeg?auto=compress&cs=tinysrgb&w=200', alt: 'Recently viewed', loading: 'lazy' } }
                    ]},
                    { type: 'p', className: 'text-xs mt-2 truncate', content: 'Silk Dupatta', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs font-semibold', content: '\u20B91,499', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[140px] md:w-[180px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=200', alt: 'Recently viewed', loading: 'lazy' } }
                    ]},
                    { type: 'p', className: 'text-xs mt-2 truncate', content: 'Cotton Palazzo', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs font-semibold', content: '\u20B91,899', style: { color: 'var(--color-primary)' } }
                  ]},
                  { type: 'div', className: 'flex-shrink-0 w-[140px] md:w-[180px] snap-start', children: [
                    { type: 'div', className: 'aspect-[3/4] overflow-hidden rounded-lg bg-gray-100', children: [
                      { type: 'img', className: 'w-full h-full object-cover', attributes: { src: 'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=200', alt: 'Recently viewed', loading: 'lazy' } }
                    ]},
                    { type: 'p', className: 'text-xs mt-2 truncate', content: 'Designer Saree', style: { color: 'var(--color-text)' } },
                    { type: 'p', className: 'text-xs font-semibold', content: '\u20B94,999', style: { color: 'var(--color-primary)' } }
                  ]}
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 35. WhatsApp CTA
  {
    category: 'CTA',
    label: 'WhatsApp CTA Banner',
    description: 'WhatsApp chat banner with green styling and direct link',
    icon: 'MessageCircle',
    block: {
      id: 'whatsapp_cta',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-8 px-4 md:py-12 md:px-8',
        style: { backgroundColor: '#dcfce7' },
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6',
            children: [
              {
                type: 'div',
                className: 'flex items-center gap-4',
                children: [
                  { type: 'div', className: 'w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0', style: { backgroundColor: '#25D366' }, children: [
                    { type: 'span', className: 'text-2xl text-white', content: '\uD83D\uDCAC' }
                  ]},
                  { type: 'div', children: [
                    { type: 'h3', className: 'text-base md:text-lg font-bold', content: '{{content.heading || "Need Help Choosing?"}}', style: { color: '#14532d' } },
                    { type: 'p', className: 'text-sm', content: '{{content.description || "Chat with our style experts on WhatsApp for personalized recommendations, size help & order tracking."}}', style: { color: '#166534' } }
                  ]}
                ]
              },
              { type: 'a', className: 'inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap', content: 'Chat on WhatsApp', attributes: { href: 'https://wa.me/919876543210', target: '_blank' }, style: { backgroundColor: '#25D366' } }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// FEATURED COLLECTION SECTIONS (3)
// ═══════════════════════════════════════════════════════════════════════

const featuredCollectionSections = [

  // 1. Collection Showcase — Product grid from a curated collection
  {
    category: 'E-commerce',
    label: 'Collection Showcase',
    description: 'Showcase a curated collection with product grid',
    icon: 'Layers',
    block: {
      id: 'collection-showcase',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 md:py-20',
        style: { backgroundColor: 'var(--color-surface)' },
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto px-4 md:px-6 lg:px-8',
            children: [
              {
                type: 'div',
                className: 'flex items-end justify-between mb-8',
                children: [
                  {
                    type: 'div',
                    children: [
                      { type: 'h2', className: 'text-2xl md:text-3xl font-bold', content: 'Featured Collection', style: { color: 'var(--color-text-primary)' } },
                      { type: 'p', className: 'text-base mt-2', content: 'Handpicked pieces for the season', style: { color: 'var(--color-text-secondary)' } }
                    ]
                  },
                  {
                    type: 'a',
                    className: 'text-sm font-medium hover:underline hidden md:block',
                    content: 'View Collection →',
                    attributes: { href: '/collections' },
                    style: { color: 'var(--color-primary)' }
                  }
                ]
              },
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6',
                loop: { data: 'content.featured_collection', itemVar: 'product' },
                children: [
                  {
                    type: 'a',
                    className: 'group',
                    attributes: { href: '/product/{{product.id}}' },
                    children: [
                      {
                        type: 'div',
                        className: 'aspect-[3/4] rounded-lg overflow-hidden mb-3',
                        children: [
                          { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500', attributes: { src: '{{product.img1}}', alt: '{{product.name}}' } }
                        ]
                      },
                      { type: 'h3', className: 'text-sm font-medium', content: '{{product.name}}', style: { color: 'var(--color-text-primary)' } },
                      { type: 'p', className: 'text-sm mt-1', content: '₹{{product.price}}', style: { color: 'var(--color-text-secondary)' } }
                    ]
                  }
                ]
              },
              {
                type: 'div',
                className: 'mt-6 text-center md:hidden',
                children: [
                  { type: 'a', className: 'text-sm font-medium hover:underline', content: 'View Full Collection →', attributes: { href: '/collections' }, style: { color: 'var(--color-primary)' } }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 2. Collection Banner — Full-width banner highlighting a collection
  {
    category: 'E-commerce',
    label: 'Collection Banner',
    description: 'Full-width banner highlighting a collection',
    icon: 'Image',
    block: {
      id: 'collection-banner',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-8 md:py-12',
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto px-4 md:px-6 lg:px-8',
            children: [
              {
                type: 'a',
                className: 'group relative block aspect-[21/9] rounded-2xl overflow-hidden',
                attributes: { href: '/collections' },
                children: [
                  { type: 'img', className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-700', attributes: { src: 'https://images.pexels.com/photos/3819793/pexels-photo-3819793.jpeg?auto=compress&cs=tinysrgb&w=1200', alt: 'Collection Banner' } },
                  { type: 'div', className: 'absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent' },
                  {
                    type: 'div',
                    className: 'absolute inset-0 flex items-center p-8 md:p-12',
                    children: [
                      {
                        type: 'div',
                        className: 'max-w-md',
                        children: [
                          { type: 'span', className: 'inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4', content: 'New Collection' },
                          { type: 'h2', className: 'text-2xl md:text-4xl font-bold text-white', content: 'The Festive Edit' },
                          { type: 'p', className: 'text-white/80 mt-2 text-sm md:text-base', content: 'Curated pieces for celebrations and special occasions' },
                          { type: 'span', className: 'inline-flex items-center gap-2 mt-4 text-white text-sm font-medium group-hover:gap-3 transition-all', content: 'Shop Collection →' }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // 3. Categories Grid — Visual category cards for browsing
  {
    category: 'E-commerce',
    label: 'Categories Grid',
    description: 'Visual category cards for browsing',
    icon: 'Grid3x3',
    block: {
      id: 'categories-grid',
      type: 'section',
      position: 99,
      enabled: true,
      visible_on: ['mobile', 'tablet', 'desktop'],
      structure: {
        type: 'section',
        className: 'py-12 md:py-20',
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto px-4 md:px-6 lg:px-8',
            children: [
              {
                type: 'div',
                className: 'text-center mb-8 md:mb-12',
                children: [
                  { type: 'h2', className: 'text-2xl md:text-3xl font-bold', content: 'Shop by Category', style: { color: 'var(--color-text-primary)' } },
                  { type: 'p', className: 'text-base mt-2', content: 'Find what you love', style: { color: 'var(--color-text-secondary)' } }
                ]
              },
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6',
                loop: { data: 'content.categories', itemVar: 'category' },
                children: [
                  {
                    type: 'a',
                    className: 'group relative aspect-[3/4] rounded-xl overflow-hidden',
                    attributes: { href: '/shop?category={{category.name}}' },
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover group-hover:scale-110 transition-transform duration-700', attributes: { src: '{{category.image}}', alt: '{{category.name}}' } },
                      { type: 'div', className: 'absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent' },
                      {
                        type: 'div',
                        className: 'absolute bottom-0 left-0 right-0 p-4',
                        children: [
                          { type: 'h3', className: 'text-white text-lg font-semibold', content: '{{category.name}}' },
                          { type: 'p', className: 'text-white/70 text-sm', content: '{{category.count}} Products' }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                type: 'div',
                className: 'text-center mt-8',
                children: [
                  { type: 'a', className: 'inline-flex items-center gap-2 text-sm font-medium hover:underline', content: 'View All Categories →', attributes: { href: '/categories' }, style: { color: 'var(--color-primary)' } }
                ]
              }
            ]
          }
        ]
      }
    }
  }
];


// ═══════════════════════════════════════════════════════════════════════
// EXPORT ALL
// ═══════════════════════════════════════════════════════════════════════

export const allSectionBlocks = [
  ...productSections,
  ...heroSections,
  ...contentSections,
  ...interactiveSections,
  ...socialProofSections,
  ...ecommerceSections,
  ...featuredCollectionSections,
];

// Category order for display
export const SECTION_BLOCK_CATEGORIES = [
  'Products',
  'Hero',
  'Content',
  'E-commerce',
  'Testimonials',
  'CTA',
  'FAQ',
  'Contact',
  'Social',
  'Stats',
  'Team',
  'Other',
];

export default allSectionBlocks;
